import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import api from "../lib/api";
import { SearchSuggest } from "../components/SearchSuggest";
import { Crown } from "lucide-react";

function StatSheet({ p, featured, testid }) {
  if (!p?.name) return null;
  return (
    <div className={`card-surface rounded-xl overflow-hidden ${featured ? "border border-[#a855f7]/50" : ""}`} data-testid={testid}>
      <div className={`px-4 py-3 flex items-center gap-3 border-b ${featured ? "bg-[#a855f7]/10 border-[#a855f7]/30" : "bg-black/40 border-white/10"}`}>
        {p.headshot && <img src={p.headshot} alt="" className="w-14 h-14 rounded-full object-cover bg-white/5" />}
        <div className="flex-1 min-w-0">
          <div className="font-display font-black uppercase tracking-wide text-lg truncate">
            {featured && <Crown size={15} className="inline text-[#a855f7] mr-1 -mt-1" />}{p.name}
            {p.jersey && <span className="text-neutral-500 text-sm font-data ml-2">#{p.jersey}</span>}
          </div>
          <div className="text-[11px] text-neutral-500">{p.pos} · {p.team}{p.ht ? ` · ${p.ht}, ${p.wt}` : ""}{p.age ? ` · Age ${p.age}` : ""}</div>
        </div>
        {featured && <span className="text-[9px] uppercase tracking-widest font-black bg-[#a855f7] text-black px-2 py-1 rounded-full shrink-0">Featured</span>}
      </div>
      {p.splits?.length > 0 ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs font-data whitespace-nowrap">
            <thead>
              <tr className="text-neutral-500 text-[10px] uppercase tracking-wider text-left border-b border-white/10">
                <th className="p-2.5 sticky left-0 bg-[#141414]">Split</th>
                {p.stat_names.map((n) => <th key={n} className="p-2.5">{n}</th>)}
              </tr>
            </thead>
            <tbody>
              {p.splits.map((s) => (
                <tr key={s.label} className="border-b border-white/5 last:border-0">
                  <td className="p-2.5 font-sans font-semibold sticky left-0 bg-[#141414] text-neutral-300">{s.label}</td>
                  {s.stats.map((v, i) => <td key={i} className="p-2.5 text-neutral-300">{v}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="p-5 text-neutral-500 text-sm">No season stats available for this player yet.</div>
      )}
    </div>
  );
}

function LeaderCard({ cat, onPick }) {
  return (
    <div className="card-surface rounded-xl overflow-hidden" data-testid={`nfl-leaders-${cat.key}`}>
      <div className="bg-[#8b1526] border-b border-[#c22040]/40 px-3 py-2">
        <span className="text-xs font-black uppercase tracking-widest text-white">{cat.label}</span>
      </div>
      {cat.leaders.map((l, i) => (
        <button key={l.id} onClick={() => onPick(l)} className="w-full flex items-center gap-2.5 px-3 py-2 border-b border-white/5 last:border-0 hover:bg-white/5 transition-colors duration-150 text-left"
          data-testid={`nfl-leader-${cat.key}-${i}`}>
          <span className={`font-data text-xs font-black w-4 shrink-0 ${i === 0 ? "text-[#a855f7]" : "text-neutral-600"}`}>{i + 1}</span>
          {l.headshot ? <img src={l.headshot} alt="" className="w-8 h-8 rounded-full object-cover bg-white/5" loading="lazy" /> : <span className="w-8 h-8 rounded-full bg-white/5" />}
          <div className="flex-1 min-w-0">
            <div className="font-semibold text-sm truncate">{l.name}</div>
            <div className="text-[10px] text-neutral-500">{l.pos}</div>
          </div>
          <span className={`font-data text-sm font-black ${i === 0 ? "gold-text" : "text-neutral-300"}`}>{l.value}</span>
        </button>
      ))}
    </div>
  );
}

export default function NFLStats() {
  const { data, isLoading } = useQuery({ queryKey: ["nfl", "playerStats"], queryFn: () => api.get("/nfl/player-stats").then((r) => r.data), staleTime: 30 * 60 * 1000 });
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const { data: search } = useQuery({
    queryKey: ["nflSearch", q],
    queryFn: () => api.get(`/nfl/player-search?q=${encodeURIComponent(q)}`).then((r) => r.data),
    enabled: q.length >= 3,
    staleTime: 10 * 60 * 1000,
  });
  const { data: selPack, isLoading: selLoading } = useQuery({
    queryKey: ["nflPlayer", sel?.id],
    queryFn: () => api.get(`/nfl/player/${sel.id}`).then((r) => r.data),
    enabled: !!sel?.id,
    staleTime: 30 * 60 * 1000,
  });
  const results = search?.players || [];
  const opts = q.length >= 3 ? results.map((p) => `${p.name} — ${p.team || "NFL"}`) : [];
  const pickFromSearch = (label) => {
    const p = results.find((x) => `${x.name} — ${x.team || "NFL"}` === label);
    if (p) { setSel(p); setQ(p.name); }
  };
  return (
    <div className="space-y-5">
      <p className="text-neutral-500 text-sm">Real season stats from ESPN{data?.season ? ` — ${data.season}` : ""}. Search any player, or tap a league leader to open their full stat sheet.</p>
      <div className="card-surface rounded-xl p-4 max-w-xl">
        <SearchSuggest value={q} onChange={setQ} onPick={pickFromSearch} options={opts}
          placeholder="Search any NFL player (min 3 letters)..." testid="nfl-stats-search" />
      </div>
      {selLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Pulling the stat sheet...</div>}
      {sel && selPack && <StatSheet p={selPack} testid="nfl-stats-selected" />}
      {isLoading && <div className="text-neutral-500 font-data text-sm animate-pulse">Loading league stats...</div>}
      {data?.featured && !sel && <StatSheet p={data.featured} featured testid="nfl-stats-featured" />}
      {data?.categories?.length > 0 && (
        <div>
          <h2 className="font-display text-lg font-bold uppercase tracking-wide text-[#a855f7] border-b border-[#a855f7]/20 pb-1 mb-3">League Leaders {data.season && <span className="text-neutral-600 text-sm">· {data.season}</span>}</h2>
          <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {data.categories.map((c) => <LeaderCard key={c.key} cat={c} onPick={(l) => { setSel(l); setQ(l.name); window.scrollTo({ top: 0, behavior: "smooth" }); }} />)}
          </div>
        </div>
      )}
    </div>
  );
}
